// Per-module allowances used by the Settings "Add operator" checkboxes and the
// left-menu gating. Keep keys in sync with backend require_access() perm keys.
export const PERMISSION_MODULES = [
  { key: "dashboard", label: "Dashboard" },
  { key: "submitted", label: "Submitted Bookings" },
  { key: "rental", label: "Rental Bookings" },
  { key: "chauffeur", label: "Chauffeur Bookings" },
  { key: "tours", label: "Tours Bookings" },
  { key: "calendar", label: "Calendar" },
  { key: "clients", label: "Clients" },
  { key: "invoices", label: "Invoices" },
  { key: "financials", label: "Financials" },
  { key: "tickets", label: "Tickets & 407 Tolls" },
  { key: "insurance", label: "Insurance Replacement" },
  { key: "fleet", label: "Fleet" },
  { key: "fleet_booking_details", label: "Fleet — Booking details (click a booking bar in Fleet Availability to open the full booking)" },
  { key: "leads", label: "Leads" },
  { key: "sales_performance", label: "Sales Performance (own numbers)" },
  { key: "sales_performance_team", label: "Sales Performance — full team (see everyone's numbers, not just their own)" },
  { key: "affiliates", label: "Affiliate Management (roster, leaderboard, challenges)" },
  { key: "import", label: "Import" },
  { key: "dealership", label: "Dealership (CRM)" },
  { key: "mechanic", label: "Mechanic Shop (CRM)" },
  { key: "my_schedule", label: "My Schedule (Driver)" },
  { key: "tasks", label: "Tasks (view assigned tasks)" },
  { key: "settings", label: "Settings / Team" },
  { key: "manage_own_signature", label: "Manage own signature (set your own signature in the My Signature menu)" },
  { key: "manage_signatures_all", label: "Manage signatures (all users) — admin oversight: view, retire, remove others' signatures" },
  { key: "chauffeur_only", label: "Chauffeur account — only sees Calendar & Fleet Availability for vehicles toggled as \"Chauffeur vehicle\" in the fleet" },
  { key: "chauffeur_submit", label: "Chauffeur account — ALSO allow submitting Chauffeur / Tour bookings (chauffeur-flagged vehicles only)" },
];

// Account types (role) an admin can pick for a login.
export const ACCOUNT_TYPES = [
  { key: "admin", label: "Admin — full access to everything" },
  { key: "staff", label: "Staff — regular login (access from the ticks below)" },
  { key: "booking_agent", label: "Booking agent — bookings + calendar" },
  { key: "redline", label: "Redline — Redline sub-fleet workflows" },
  { key: "affiliate", label: "Affiliate — car availability only" },
  { key: "chauffeur", label: "Chauffeur — driver-only account" },
  { key: "investor", label: "Investor — only their own cars' bookings & calendar" },
];

// Sensible default allowances for a brand-new non-admin operator (typical staff).
export const DEFAULT_PERMISSIONS = [
  "dashboard", "submitted", "rental", "chauffeur", "tours",
  "calendar", "clients", "fleet", "leads", "import",
  "manage_own_signature",
];

// Whether a nav item with a given perm key is visible for a user. Accepts a
// single perm key OR an array (any-of). Returns true/false; if the user is a
// legacy account (no permissions array), returns null so the caller can fall
// back to the old role list.
export function permVisible(user, perm) {
  if (!user) return false;
  if (user.role === "admin") return true;
  // Investor accounts are hard-locked to the investor bookings screen. No perm
  // gates them in — the sidebar filters them by role explicitly.
  if (user.role === "investor") return false;
  // An EMPTY permissions array on a role-based account (booking_agent, redline,
  // affiliate) means "no custom profile" — fall back to the legacy role map so
  // their built-in tabs (e.g. Calendar) don't vanish. Staff keeps [] = nothing.
  if (Array.isArray(user.permissions) && user.permissions.length === 0 && user.role !== "staff") {
    return null;
  }
  if (Array.isArray(user.permissions)) {
    // Chauffeur-only accounts see ONLY Calendar & Fleet Availability, even if
    // other perms are somehow granted — UNLESS `chauffeur_submit` is also
    // granted (then they also get New Booking / My Bookings / Rejected).
    if (isChauffeurOnly(user)) {
      const base = perm === "calendar" || perm === "fleet";
      if (base) return true;
      if (canChauffeurSubmit(user)) {
        return perm === "chauffeur_submit_nav";
      }
      return false;
    }
    const needed = Array.isArray(perm) ? perm : [perm];
    return needed.some((p) => user.permissions.includes(p));
  }
  return null; // legacy
}

// True when this user's permissions include the chauffeur_only flag (and they
// are not an admin — admins are never restricted).
export function isChauffeurOnly(user) {
  if (!user || user.role === "admin") return false;
  return Array.isArray(user.permissions) && user.permissions.includes("chauffeur_only");
}

// True when a chauffeur-only account has the `chauffeur_submit` add-on that
// lets them submit chauffeur / tour bookings against chauffeur-flagged cars.
export function canChauffeurSubmit(user) {
  if (!isChauffeurOnly(user)) return false;
  return Array.isArray(user.permissions) && user.permissions.includes("chauffeur_submit");
}

// Maps a route path to the permission key that grants access to it. Checked in
// order; the first whose prefix matches the pathname wins. Keep in sync with the
// sidebar nav perms in Layout.jsx.
// Each entry may list one perm (`perm`) or multiple accepted perms (`perms`).
const ROUTE_PERMS = [
  { p: "/bookings/submitted", perm: "submitted" },
  { p: "/bookings/rental", perm: "rental" },
  { p: "/bookings/chauffeur", perm: "chauffeur" },
  { p: "/bookings/tours", perm: "tours" },
  { p: "/bookings/pre", perm: "rental" },
  // /bookings/view is reachable via full booking tabs OR via the Fleet
  // Availability click-through — accept either permission.
  { p: "/bookings/view", perms: ["submitted", "fleet_booking_details"] },
  { p: "/calendar", perm: "calendar" },
  { p: "/customers", perm: "clients" },
  { p: "/invoices", perm: "invoices" },
  { p: "/financials", perm: "financials" },
  { p: "/tickets", perm: "tickets" },
  { p: "/insurance", perm: "insurance" },
  { p: "/fleet", perm: "fleet" },
  { p: "/availability", perm: "fleet" },
  { p: "/chauffeurs", perm: "chauffeur" },
  { p: "/tours", perm: "tours" },
  { p: "/leads", perm: "leads" },
  { p: "/sales-performance", perms: ["sales_performance", "sales_performance_team"] },
  { p: "/affiliate-management", perm: "affiliates" },
  { p: "/leaderboard", perm: "affiliates" },
  { p: "/import", perm: "import" },
  { p: "/my-schedule", perm: "my_schedule" },
  { p: "/tasks", perm: "tasks" },
  { p: "/settings", perm: "settings" },
  { p: "/new-booking", perm: "rental" },
  { p: "/my-bookings", perm: "submitted" },
  { p: "/rejected", perm: "submitted" },
];

// Returns the permission key required for a given pathname, or null if the route
// has no associated permission (e.g. the dashboard root "/"). May return either
// a single string or an array of accepted perms (any-of).
export function permForPath(pathname) {
  if (pathname === "/") return "dashboard";
  const hit = ROUTE_PERMS.find(
    (r) => pathname === r.p || pathname.startsWith(r.p + "/")
  );
  if (!hit) return null;
  return hit.perms || hit.perm;
}

// The only routes a chauffeur-only user may visit. When the `chauffeur_submit`
// add-on is also granted, additional booking-submission routes are appended in
// the userHomePath / App.js guard so they can create/edit their own bookings.
export const CHAUFFEUR_ONLY_PATHS = ["/calendar", "/availability", "/settings"];
export const CHAUFFEUR_SUBMIT_PATHS = [
  "/new-booking", "/my-bookings", "/rejected", "/bookings/view",
];

// First route a permission-managed user is allowed to land on. Used as the
// redirect target when they hit a page they cannot access.
const HOME_CANDIDATES = [
  ["/", "dashboard"],
  ["/bookings/submitted", "submitted"],
  ["/bookings/rental", "rental"],
  ["/calendar", "calendar"],
  ["/availability", "fleet"],
  ["/leads", "leads"],
  ["/tickets", "tickets"],
  ["/insurance", "insurance"],
  ["/my-schedule", "my_schedule"],
  ["/tasks", "tasks"],
  ["/mechanic", "mechanic"],
  ["/dealership", "dealership"],
  ["/settings", "settings"],
];

export function userHomePath(user) {
  if (!user) return "/login";
  if (user.role === "admin") return "/";
  if (isChauffeurOnly(user)) return "/calendar";
  if (Array.isArray(user.permissions)) {
    const hit = HOME_CANDIDATES.find(([, perm]) => user.permissions.includes(perm));
    return hit ? hit[0] : "/settings";
  }
  return "/";
}
